import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  UserProfile,
  Conversation,
  ChatMessage,
  Story,
  Moment,
  VaultItem,
  CallLog,
  ActiveCallState,
  MainNavigationTab,
  PulseStatus,
} from './types';
import { getLocalUser, setLocalUser, getLocalSettings, setLocalSettings, AppSettings } from './lib/storage';
import { soundEffects } from './lib/soundEffects';
import { executeWateenAIChat } from './lib/gemini';
import { subscribeToWateenChannel, broadcastMessageToSupabase } from './lib/supabase';

import { WateenHeader } from './components/WateenHeader';
import { WateenNavigation } from './components/WateenNavigation';
import { StoriesCarousel } from './components/StoriesCarousel';
import { ConversationsList } from './components/ConversationsList';
import { ChatView } from './components/ChatView';
import { MomentsView } from './components/MomentsView';
import { VaultView } from './components/VaultView';
import { DiscoverView } from './components/DiscoverView';
import { CallsView } from './components/CallsView';
import { SettingsView } from './components/SettingsView';
import { AdminView } from './components/AdminView';
import { PremiumView } from './components/PremiumView';
import { PremiumModal } from './components/PremiumModal';
import { AuthModal } from './components/AuthModal';
import { CallModal } from './components/CallModal';
import { UserProfileModal } from './components/UserProfileModal';
import { AIAssistantModal } from './components/AIAssistantModal';
import { WateenHubView } from './components/WateenHubView';
import { ErrorBoundary } from './components/ErrorBoundary';
import {
  CreateStoryModal,
  CreateMomentModal,
  CreateGroupModal,
  GlobalSearchModal,
} from './components/CreateModals';

export default function App() {
  // --- USER & SETTINGS STATE ---
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(getLocalUser);
  const [appSettings, setAppSettings] = useState<AppSettings>(getLocalSettings);

  // --- NAVIGATION & TABS ---
  const [activeTab, setActiveTab] = useState<MainNavigationTab>('chats');
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

  // --- DATA LISTS ---
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messagesMap, setMessagesMap] = useState<Record<string, ChatMessage[]>>({});
  const [stories, setStories] = useState<Story[]>([]);
  const [moments, setMoments] = useState<Moment[]>([]);
  const [vaultItems, setVaultItems] = useState<VaultItem[]>([]);
  const [calls, setCalls] = useState<CallLog[]>([]);

  // --- PRESENCE ---
  const [onlinePresence, setOnlinePresence] = useState<
    Record<string, { isOnline: boolean; isTyping: boolean }>
  >({});

  // --- MODALS & OVERLAYS ---
const [showAuthModal, setShowAuthModal] = useState(!currentUser);
  const [activeCall, setActiveCall] = useState<ActiveCallState | null>(null);
  const [selectedProfileUsername, setSelectedProfileUsername] = useState<string | null>(null);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [showCreateStory, setShowCreateStory] = useState(false);
  const [showCreateMoment, setShowCreateMoment] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const sseRef = useRef<EventSource | null>(null);
  const initialAutoSelectDoneRef = useRef(false);

  // Save settings change
  const handleUpdateSettings = (newSettings: Partial<AppSettings>) => {
    const updated = { ...appSettings, ...newSettings };
    setAppSettings(updated);
    setLocalSettings(updated);
    if (newSettings.soundEnabled !== undefined) {
      soundEffects.enabled = newSettings.soundEnabled;
    }
  };

  // --- FETCH INITIAL DATA ---
  const loadInitialData = useCallback(async (user: UserProfile) => {
    try {
      // 1. Fetch Conversations
      const convsRes = await fetch(`/api/conversations?username=${encodeURIComponent(user.username)}`);
      const convsData = await convsRes.json();
      if (convsData.conversations) {
        let initialAiLastMsg: ChatMessage = {
          id: 'msg-wateen-1',
          conversationId: 'wateen-ai-chat',
          senderId: 'wateen-ai',
          senderUsername: 'wateen-ai',
          senderName: 'وتين الذكي (AI)',
          senderAvatar: 'https://api.dicebear.com/9.x/bottts/svg?seed=Wateen',
          text: 'مرحباً بك! أنا وتين الذكي 🤖، كيف يمكنني مساعدتك اليوم؟',
          timestamp: Date.now(),
          status: 'sent'
        };

        // Check cached AI messages
        const cachedAIMsgs = localStorage.getItem(`wateen_ai_msgs_${user.username}`);
        if (cachedAIMsgs) {
          try {
            const parsed = JSON.parse(cachedAIMsgs);
            if (Array.isArray(parsed) && parsed.length > 0) {
              setMessagesMap((prev) => ({ ...prev, ['wateen-ai-chat']: parsed }));
              initialAiLastMsg = parsed[parsed.length - 1];
            }
          } catch {}
        }

        const wateenAIChat: Conversation = {
          id: 'wateen-ai-chat',
          type: 'ai',
          title: 'وتين الذكي (AI)',
          avatar: 'https://api.dicebear.com/9.x/bottts/svg?seed=Wateen',
          participants: [user.username, 'wateen-ai'],
          createdAt: Date.now(),
          lastMessage: initialAiLastMsg,
          unreadCount: 0,
          isPinned: true
        };
        
        let validConvs = Array.isArray(convsData.conversations) ? convsData.conversations : [];
        // Deduplicate conversations and ensure wateen-ai is pinned at the top
        const seenConvs = new Set<string>();
        seenConvs.add(wateenAIChat.id);
        const uniqueConvs: Conversation[] = [wateenAIChat];
        for (const c of validConvs) {
          if (c && c.id && !seenConvs.has(c.id)) {
            seenConvs.add(c.id);
            uniqueConvs.push(c);
          }
        }

        setConversations(uniqueConvs);
        
        if (!initialAutoSelectDoneRef.current) {
          initialAutoSelectDoneRef.current = true;
          if (typeof window !== 'undefined' && window.innerWidth >= 1024 && uniqueConvs.length > 0) {
            setActiveConversationId(uniqueConvs[0].id);
          }
        }
      }

      // 2. Fetch Stories
      const storiesRes = await fetch('/api/stories');
      const storiesData = await storiesRes.json();
      if (storiesData.stories) setStories(storiesData.stories);

      // 3. Fetch Moments
      const momentsRes = await fetch('/api/moments');
      const momentsData = await momentsRes.json();
      if (momentsData.moments) setMoments(momentsData.moments);

      // 4. Fetch Vault
      const vaultRes = await fetch(`/api/vault?username=${encodeURIComponent(user.username)}`);
      const vaultData = await vaultRes.json();
      if (vaultData.vault) setVaultItems(vaultData.vault);

      // 5. Fetch Calls
      const callsRes = await fetch('/api/calls');
      const callsData = await callsRes.json();
      if (callsData.calls) setCalls(callsData.calls);

      // 6. Sync Premium Subscription Status
      try {
        const subRes = await fetch(`/api/subscription/status?username=${encodeURIComponent(user.username)}`);
        if (subRes.ok) {
          const subData = await subRes.json();
          if (subData.user) {
            setCurrentUser((prev) => (prev ? { ...prev, ...subData.user } : prev));
            setLocalUser(subData.user);
          }
        }
      } catch (subErr) {
        console.warn('Subscription sync error:', subErr);
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
    }
  }, []);

  // Load active conversation messages
  const loadConversationMessages = useCallback(async (convId: string) => {
    try {
      const userParam = currentUser?.username ? `?username=${encodeURIComponent(currentUser.username)}` : '';
      const res = await fetch(`/api/conversations/${convId}/messages${userParam}`);
      const data = await res.json();
      if (data.messages && Array.isArray(data.messages)) {
        setMessagesMap((prev) => {
          if (convId === 'wateen-ai-chat') {
            if (data.messages.length === 0 && (prev[convId]?.length || 0) > 0) {
              return prev;
            }
            if (currentUser?.username) {
              try {
                localStorage.setItem(`wateen_ai_msgs_${currentUser.username}`, JSON.stringify(data.messages));
              } catch {}
            }
          }
          return { ...prev, [convId]: data.messages };
        });
      }
    } catch (e) {
      console.error('Error loading messages for', convId, e);
    }
  }, [currentUser?.username]);

  useEffect(() => {
    if (activeConversationId) {
      loadConversationMessages(activeConversationId);
    }
  }, [activeConversationId, loadConversationMessages]);

  // --- SSE REAL-TIME SETUP ---
  useEffect(() => {
    if (!currentUser) return;

    loadInitialData(currentUser);

    // Setup SSE connection
    const streamUrl = `/api/chat/stream?username=${encodeURIComponent(currentUser.username)}`;
    const eventSource = new EventSource(streamUrl);
    sseRef.current = eventSource;

    // Setup Supabase Realtime Channel
    const supabaseChannel = subscribeToWateenChannel('wateen_global_channel', {
      onMessage: (message) => {
        if (!message || !message.conversationId) return;
        setMessagesMap((prev) => {
          const currentList = prev[message.conversationId] || [];
          if (currentList.some((m) => m.id === message.id)) return prev;
          return { ...prev, [message.conversationId]: [...currentList, message] };
        });

        setConversations((prev) =>
          prev.map((c) => (c.id === message.conversationId ? { ...c, lastMessage: message } : c))
        );

        if (message.senderUsername !== currentUser.username) {
          soundEffects.playReceived();
        }
      },
      onTyping: ({ username, isTyping }) => {
        setOnlinePresence((prev) => ({
          ...prev,
          [username]: { ...prev[username], isTyping, isOnline: true, lastSeen: Date.now() },
        }));
      },
    });

    eventSource.addEventListener('new_message', (e: MessageEvent) => {
      try {
        const { conversationId, message } = JSON.parse(e.data);
        setMessagesMap((prev) => {
          const currentList = prev[conversationId] || [];
          if (currentList.some((m) => m.id === message.id)) return prev;
          return { ...prev, [conversationId]: [...currentList, message] };
        });

        // Update last message of conversation
        setConversations((prev) =>
          prev.map((c) => (c.id === conversationId ? { ...c, lastMessage: message } : c))
        );

        // Sound chime if message from another user
        if (message.senderUsername !== currentUser.username) {
          soundEffects.playReceived();
        }
      } catch (err) {
        console.error('Error handling new_message SSE:', err);
      }
    });

    eventSource.addEventListener('message_edited', (e: MessageEvent) => {
      try {
        const { conversationId, message } = JSON.parse(e.data);
        setMessagesMap((prev) => ({
          ...prev,
          [conversationId]: (prev[conversationId] || []).map((m) =>
            m.id === message.id ? message : m
          ),
        }));
      } catch {}
    });

    eventSource.addEventListener('message_deleted', (e: MessageEvent) => {
      try {
        const { conversationId, id, forEveryone, updatedMessage } = JSON.parse(e.data);
        setMessagesMap((prev) => ({
          ...prev,
          [conversationId]: forEveryone
            ? (prev[conversationId] || []).map((m) => (m.id === id ? updatedMessage : m))
            : (prev[conversationId] || []).filter((m) => m.id !== id),
        }));
      } catch {}
    });

    eventSource.addEventListener('messages_read', (e: MessageEvent) => {
      try {
        const { conversationId, readerUsername } = JSON.parse(e.data);
        setMessagesMap((prev) => ({
          ...prev,
          [conversationId]: (prev[conversationId] || []).map((m) =>
            m.senderUsername !== readerUsername && m.status !== 'read'
              ? { ...m, status: 'read' }
              : m
          ),
        }));
        setConversations((prev) => 
          prev.map((c) => {
            if (c.id === conversationId && c.lastMessage && c.lastMessage.senderUsername !== readerUsername) {
              return {
                ...c,
                lastMessage: { ...c.lastMessage, status: 'read' }
              };
            }
            return c;
          })
        );
      } catch {}
    });

    eventSource.addEventListener('reaction_updated', (e: MessageEvent) => {
      try {
        const { conversationId, messageId, reactions } = JSON.parse(e.data);
        setMessagesMap((prev) => ({
          ...prev,
          [conversationId]: (prev[conversationId] || []).map((m) =>
            m.id === messageId ? { ...m, reactions } : m
          ),
        }));
      } catch {}
    });

    eventSource.addEventListener('presence', (e: MessageEvent) => {
      try {
        const { username, presence } = JSON.parse(e.data);
        setOnlinePresence((prev) => ({ ...prev, [username]: presence }));
      } catch {}
    });

    eventSource.addEventListener('partner_typing', (e: MessageEvent) => {
      try {
        const { conversationId, username, isTyping } = JSON.parse(e.data);
        setOnlinePresence((prev) => ({
          ...prev,
          [username]: { ...prev[username], isTyping, isOnline: true, lastSeen: Date.now() },
        }));
      } catch {}
    });

    eventSource.addEventListener('conversation_deleted', (e: MessageEvent) => {
      try {
        const { conversationId } = JSON.parse(e.data);
        setConversations((prev) => prev.filter((c) => c.id !== conversationId));
        setActiveConversationId((prev) => (prev === conversationId ? null : prev));
      } catch {}
    });

    eventSource.addEventListener('conversation_cleared', (e: MessageEvent) => {
      try {
        const { conversationId } = JSON.parse(e.data);
        setMessagesMap((prev) => ({
          ...prev,
          [conversationId]: [],
        }));
      } catch {}
    });

    eventSource.addEventListener('story_added', (e: MessageEvent) => {
      try {
        const newStory = JSON.parse(e.data);
        setStories((prev) => [newStory, ...prev.filter((s) => s.id !== newStory.id)]);
      } catch {}
    });

    eventSource.addEventListener('story_deleted', (e: MessageEvent) => {
      try {
        const { id } = JSON.parse(e.data);
        setStories((prev) => prev.filter((s) => s.id !== id));
      } catch {}
    });

    eventSource.addEventListener('story_updated', (e: MessageEvent) => {
      try {
        const updatedStory = JSON.parse(e.data);
        setStories((prev) => prev.map((s) => (s.id === updatedStory.id ? updatedStory : s)));
      } catch {}
    });

    eventSource.addEventListener('moment_added', (e: MessageEvent) => {
      try {
        const newMoment = JSON.parse(e.data);
        setMoments((prev) => [newMoment, ...prev.filter((m) => m.id !== newMoment.id)]);
      } catch {}
    });

    eventSource.addEventListener('conversation_created', (e: MessageEvent) => {
      try {
        const newConv = JSON.parse(e.data);
        setConversations((prev) => [newConv, ...prev.filter((c) => c.id !== newConv.id)]);
      } catch {}
    });

    eventSource.addEventListener('conversation_updated', (e: MessageEvent) => {
      try {
        const updatedConv = JSON.parse(e.data);
        setConversations((prev) => prev.map((c) => (c.id === updatedConv.id ? updatedConv : c)));
      } catch {}
    });

    // Heartbeat presence interval
    const heartbeat = setInterval(() => {
      fetch('/api/chat/presence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: currentUser.username, isOnline: true }),
      }).catch(() => {});
    }, 15000);

    return () => {
      eventSource.close();
      supabaseChannel.unsubscribe();
      clearInterval(heartbeat);
    };
  }, [currentUser, loadInitialData]);

  // Support mobile hardware/browser back button to exit conversation
  useEffect(() => {
    if (!activeConversationId) return;

    // Do NOT push state for 'wateen-ai-chat' to prevent breaking client-side AI state on popstate
    if (activeConversationId === 'wateen-ai-chat') {
        return;
    }

    window.history.pushState({ wateenChat: activeConversationId }, '');

    const handlePopState = () => {
      setActiveConversationId(null);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [activeConversationId]);

  // --- ACTIONS ---

  const handleSendMessage = async (msgData: Partial<ChatMessage>) => {
    if (!currentUser || !activeConversationId) return;

    const fullMessage = {
      ...msgData,
      senderId: currentUser.id,
      senderUsername: currentUser.username,
      senderName: currentUser.displayName,
      senderAvatar: currentUser.avatar,
    };

    // Client-side AI handler for production deployment
    if (activeConversationId === 'wateen-ai-chat') {
      const userMsg: ChatMessage = {
        id: `msg_user_${Date.now()}`,
        conversationId: activeConversationId,
        senderId: currentUser.id,
        senderUsername: currentUser.username,
        senderName: currentUser.displayName,
        senderAvatar: currentUser.avatar || '',
        text: msgData.text || '',
        timestamp: Date.now(),
        status: 'read'
      };

      setMessagesMap((prev) => {
        const nextList = [...(prev['wateen-ai-chat'] || []), userMsg];
        if (currentUser.username) {
          try {
            localStorage.setItem(`wateen_ai_msgs_${currentUser.username}`, JSON.stringify(nextList));
          } catch {}
        }
        return {
          ...prev,
          ['wateen-ai-chat']: nextList,
        };
      });

      setConversations((prev) =>
        prev.map((c) => (c.id === 'wateen-ai-chat' ? { ...c, lastMessage: userMsg } : c))
      );

      soundEffects.playSent();

      setOnlinePresence((prev) => ({
        ...prev,
        'wateen-ai': { isOnline: true, isTyping: true }
      }));

      try {
        const existingHistory = (messagesMap['wateen-ai-chat'] || [])
          .slice(-6)
          .map((m) => ({
            role: m.senderUsername === 'wateen-ai' ? 'model' : 'user',
            text: m.text,
          }));

        // Directly call client-side Gemini (powered by VITE_GEMINI_API_KEY)
        const replyText = await executeWateenAIChat({
          prompt: msgData.text,
          history: existingHistory,
          username: currentUser?.username,
        });

        const aiMsg: ChatMessage = {
          id: `msg_ai_${Date.now()}`,
          conversationId: activeConversationId,
          senderId: 'wateen-ai',
          senderUsername: 'wateen-ai',
          senderName: 'وتين الذكي (AI)',
          senderAvatar: 'https://api.dicebear.com/9.x/bottts/svg?seed=Wateen',
          text: replyText,
          timestamp: Date.now(),
          status: 'sent'
        };

        setOnlinePresence((prev) => ({
          ...prev,
          'wateen-ai': { isOnline: true, isTyping: false }
        }));

        setMessagesMap((prev) => {
          const nextList = [...(prev['wateen-ai-chat'] || []), aiMsg];
          if (currentUser.username) {
            try {
              localStorage.setItem(`wateen_ai_msgs_${currentUser.username}`, JSON.stringify(nextList));
            } catch {}
          }
          return {
            ...prev,
            ['wateen-ai-chat']: nextList,
          };
        });

        setConversations((prev) =>
          prev.map((c) => (c.id === 'wateen-ai-chat' ? { ...c, lastMessage: aiMsg } : c))
        );

        soundEffects.playReceived();
      } catch (err) {
        setOnlinePresence((prev) => ({
          ...prev,
          'wateen-ai': { isOnline: true, isTyping: false }
        }));
        console.warn("AI chat error handled gracefully:", err);
        const fallbackMsg: ChatMessage = {
          id: `msg_ai_${Date.now()}`,
          conversationId: activeConversationId,
          senderId: 'wateen-ai',
          senderUsername: 'wateen-ai',
          senderName: 'وتين الذكي (AI)',
          senderAvatar: 'https://api.dicebear.com/9.x/bottts/svg?seed=Wateen',
          text: 'أهلاً بك! أنا وتين الذكي 🤖. يسعدني دائماً التواصل معك ومساعدتك.',
          timestamp: Date.now(),
          status: 'sent'
        };
        setMessagesMap((prev) => ({
          ...prev,
          ['wateen-ai-chat']: [...(prev['wateen-ai-chat'] || []), fallbackMsg],
        }));
      }
      return;
    }

    try {
      const res = await fetch(`/api/conversations/${activeConversationId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fullMessage),
      });
      const data = await res.json();
      if (data.message) {
        // Broadcast through Supabase Realtime
        broadcastMessageToSupabase('wateen_global_channel', data.message);

        setMessagesMap((prev) => {
          const currentList = prev[activeConversationId] || [];
          if (currentList.some((m) => m.id === data.message.id)) return prev;
          return {
            ...prev,
            [activeConversationId]: [...currentList, data.message],
          };
        });
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleDeleteMessage = async (msgId: string, forEveryone: boolean) => {
    if (!activeConversationId) return;
    try {
      await fetch(`/api/conversations/${activeConversationId}/messages/${msgId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ forEveryone }),
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleReactMessage = async (msgId: string, emoji: string) => {
    if (!currentUser || !activeConversationId) return;
    try {
      await fetch(`/api/conversations/${activeConversationId}/messages/${msgId}/reaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emoji,
          username: currentUser.username,
          displayName: currentUser.displayName,
        }),
      });
      soundEffects.playTap();
    } catch (err) {
      console.error(err);
    }
  };

  const handleTyping = (isTyping: boolean) => {
    if (!currentUser) return;
    fetch('/api/chat/presence', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: currentUser.username, isOnline: true, isTyping }),
    }).catch(() => {});
  };

  const handlePinMessage = async (msgId: string) => {
    if (!activeConversationId) return;
    try {
      await fetch(`/api/conversations/${activeConversationId}/messages/${msgId}/pin`, {
        method: 'POST',
      });
      soundEffects.playTap();
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdatePulse = async (pulse: PulseStatus) => {
    if (!currentUser) return;
    try {
      const res = await fetch('/api/users/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: currentUser.username, pulseStatus: pulse }),
      });
      const data = await res.json();
      if (data.user) {
        setCurrentUser(data.user);
        setLocalUser(data.user);
        soundEffects.playTap();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpdateProfile = async (profileData: Partial<UserProfile>) => {
    if (!currentUser) return;
    try {
      const res = await fetch('/api/users/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: currentUser.username, ...profileData }),
      });
      const data = await res.json();
      if (data.user) {
        setCurrentUser(data.user);
        setLocalUser(data.user);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddVaultItem = async (item: Partial<VaultItem>) => {
    if (!currentUser) return;
    try {
      const res = await fetch('/api/vault', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...item, username: currentUser.username }),
      });
      const data = await res.json();
      if (data.item) {
        setVaultItems((prev) => [data.item, ...prev]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteVaultItem = async (id: string) => {
    try {
      await fetch(`/api/vault/${id}`, { method: 'DELETE' });
      setVaultItems((prev) => prev.filter((v) => v.id !== id));
      soundEffects.playTap();
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveToVaultFromChat = (
    title: string,
    content: string,
    type: 'message' | 'media' | 'note'
  ) => {
    handleAddVaultItem({ title, content, type, folder: 'رسائل وتين' });
  };

  // Start Direct Chat with user
  const handleStartDirectChat = async (targetUsername: string) => {
    if (!currentUser) return;
    try {
      const res = await fetch('/api/conversations/direct', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromUsername: currentUser.username,
          targetUsername,
        }),
      });
      const data = await res.json();
      if (data.conversation) {
        setConversations((prev) => [
          data.conversation,
          ...prev.filter((c) => c.id !== data.conversation.id),
        ]);
        setActiveConversationId(data.conversation.id);
        setActiveTab('chats');
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Start Call
  const handleStartCall = (
    targetUser: { username: string; displayName: string; avatar: string },
    type: 'voice' | 'video'
  ) => {
    setActiveCall({
      isOpen: true,
      targetUser,
      type,
      status: 'ringing',
      isMuted: false,
      isVideoEnabled: type === 'video',
      isSpeaker: true,
      durationSec: 0,
    });
  };

  const handleEndCall = () => {
    if (activeCall) {
      // Log call
      fetch('/api/calls/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetUser: activeCall.targetUser,
          type: activeCall.type,
          direction: 'outgoing',
          durationSec: 45,
        }),
      }).then(() => {
        // reload calls
        fetch('/api/calls')
          .then((r) => r.json())
          .then((d) => d.calls && setCalls(d.calls));
      });
    }
    setActiveCall(null);
  };

  // Create Story
  const handleCreateStory = async (storyData: any) => {
    try {
      const res = await fetch('/api/stories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(storyData),
      });
      const data = await res.json();
      if (data.story) {
        setStories((prev) => [data.story, ...prev]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleToggleArchive = async (conversationId: string, archived: boolean) => {
    if (!currentUser) return;
    try {
      await fetch(`/api/conversations/${conversationId}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: currentUser.username, archived }),
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteStory = async (storyId: string) => {
    if (!currentUser) return;
    try {
      await fetch(`/api/stories/${storyId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: currentUser.username }),
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleEditStory = async (
    storyId: string,
    updates: { caption?: string; mood?: string; music?: string } | string
  ) => {
    if (!currentUser) return;
    try {
      const payload =
        typeof updates === 'string'
          ? { username: currentUser.username, caption: updates }
          : { username: currentUser.username, ...updates };

      const res = await fetch(`/api/stories/${storyId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.story) {
          setStories((prev) => prev.map((s) => (s.id === storyId ? data.story : s)));
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Delete Conversation
  const handleDeleteConversation = async (convId: string) => {
    if (!currentUser) return;
    try {
      const res = await fetch(`/api/conversations/${convId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: currentUser.username }),
      });
      if (res.ok) {
        setConversations((prev) => prev.filter((c) => c.id !== convId));
        setMessagesMap((prev) => {
          const next = { ...prev };
          delete next[convId];
          return next;
        });
        if (activeConversationId === convId) {
          setActiveConversationId(null);
        }
        soundEffects.playTap();
      }
    } catch (e) {
      console.error('Failed to delete conversation:', e);
    }
  };

  // Clear Conversation messages
  const handleClearConversation = async (convId: string) => {
    if (!currentUser) return;
    try {
      const res = await fetch(`/api/conversations/${convId}/clear`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: currentUser.username }),
      });
      if (res.ok) {
        setMessagesMap((prev) => ({
          ...prev,
          [convId]: [],
        }));
        setConversations((prev) =>
          prev.map((c) =>
            c.id === convId
              ? {
                  ...c,
                  lastMessage: undefined,
                }
              : c
          )
        );
        soundEffects.playTap();
      }
    } catch (e) {
      console.error('Failed to clear conversation:', e);
    }
  };

  // Block user
  const handleBlockUser = async (targetUsername: string) => {
    if (!currentUser) return;
    try {
      const res = await fetch('/api/users/block', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: currentUser.username,
          targetUsername,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        const updatedBlocked = data.blockedUsers || [
          ...(currentUser.blockedUsers || []),
          targetUsername,
        ];
        const updatedUser = { ...currentUser, blockedUsers: updatedBlocked };
        setCurrentUser(updatedUser);
        localStorage.setItem('wateen_user', JSON.stringify(updatedUser));
        soundEffects.playTap();
      }
    } catch (e) {
      console.error('Failed to block user:', e);
    }
  };

  // Unblock user
  const handleUnblockUser = async (targetUsername: string) => {
    if (!currentUser) return;
    try {
      const res = await fetch('/api/users/unblock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: currentUser.username,
          targetUsername,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        const updatedBlocked =
          data.blockedUsers || (currentUser.blockedUsers || []).filter((u) => u !== targetUsername);
        const updatedUser = { ...currentUser, blockedUsers: updatedBlocked };
        setCurrentUser(updatedUser);
        localStorage.setItem('wateen_user', JSON.stringify(updatedUser));
        soundEffects.playTap();
      }
    } catch (e) {
      console.error('Failed to unblock user:', e);
    }
  };

  // Create Moment
  const handleCreateMoment = async (momentData: any) => {
    try {
      const res = await fetch('/api/moments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(momentData),
      });
      const data = await res.json();
      if (data.moment) {
        setMoments((prev) => [data.moment, ...prev]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Like Moment
  const handleLikeMoment = async (momentId: string) => {
    try {
      await fetch(`/api/moments/${momentId}/like`, { method: 'POST' });
    } catch (e) {
      console.error(e);
    }
  };

  // Create Group or Channel
  const handleCreateGroup = async (groupData: any) => {
    try {
      const res = await fetch('/api/conversations/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(groupData),
      });
      const data = await res.json();
      if (data.conversation) {
        setConversations((prev) => [
          data.conversation,
          ...prev.filter((c) => c.id !== data.conversation.id),
        ]);
        setActiveConversationId(data.conversation.id);
        setActiveTab('chats');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleLogout = () => {
    setLocalUser(null);
    setCurrentUser(null);
    setShowAuthModal(true);
  };

  const handleTerminateSession = async (deviceId: string) => {
    if (!currentUser) return;
    await fetch('/api/auth/terminate-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: currentUser.username, deviceId }),
    });
    // refresh profile
    fetch(`/api/users/${currentUser.username}`)
      .then((r) => r.json())
      .then((d) => d.user && setCurrentUser(d.user));
  };

  const handleLogoutAllDevices = async () => {
    if (!currentUser) return;
    const currentDevice = currentUser.activeSessions?.[0]?.deviceId || 'cur';
    await fetch('/api/auth/logout-all', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: currentUser.username, currentDeviceId: currentDevice }),
    });
    soundEffects.playTap();
    // refresh profile
    fetch(`/api/users/${currentUser.username}`)
      .then((r) => r.json())
      .then((d) => d.user && setCurrentUser(d.user));
  };

  // Memoized deduplicated conversations to guarantee strictly unique keys and references across the whole app
  const deduplicatedConversations = useMemo(() => {
    const seen = new Set<string>();
    return conversations.filter((c) => {
      if (!c || !c.id || seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }, [conversations]);

  // Active conversation object
  const activeConversation = deduplicatedConversations.find((c) => c.id === activeConversationId);
  const activeMessages = activeConversation ? messagesMap[activeConversation.id] || [] : [];
  const otherUsername = activeConversation?.participants.find((p) => p !== currentUser?.username);
  const partnerOnline = otherUsername ? onlinePresence[otherUsername]?.isOnline : false;
  const isPartnerTyping = otherUsername ? onlinePresence[otherUsername]?.isTyping : false;

  // Unread messages count
  const unreadCount = deduplicatedConversations.reduce((acc, c) => acc + (c.unreadCount || 0), 0);

  // Theme styles
  const themeClasses: Record<string, string> = {
    dark: 'bg-[#06080e] text-slate-100',
    midnight: 'bg-[#070b18] text-slate-100',
    cyber: 'bg-[#040e0b] text-emerald-100',
    light: 'bg-[#06080e] text-slate-100',
    royal_gold: 'bg-[#0b0802] text-amber-50',
    cosmic_nebula: 'bg-[#080512] text-indigo-50',
    neon_emerald: 'bg-[#030d09] text-emerald-50',
    dark_diamond: 'bg-[#030303] text-slate-100',
  };

  return (
    <div
      dir="rtl"
      className={`h-full w-full flex flex-col overflow-hidden font-['Cairo'] antialiased ${
        themeClasses[appSettings?.theme || 'dark'] || themeClasses.dark
      }`}
    >
      {/* 1. TOP HEADER */}
      {currentUser && (
        <WateenHeader
          currentUser={currentUser}
          onOpenProfile={() => setSelectedProfileUsername(currentUser.username)}
          onOpenSearch={() => setShowGlobalSearch(true)}
          onOpenNewDirectChat={() => setShowGlobalSearch(true)}
          onOpenNewChat={() => setShowCreateGroup(true)}
          onOpenAI={() => setShowAIAssistant(true)}
          onOpenHub={() => {
            setActiveTab('hub');
            soundEffects.playTap();
          }}
          onUpdatePulse={handleUpdatePulse}
          onOpenPremium={() => setShowPremiumModal(true)}
        />
      )}

      {/* 2. MAIN APP SHELL */}
      <div className="flex-1 flex overflow-hidden pb-14 sm:pb-0">
        {/* Navigation Sidebar / Bottom Bar */}
        {currentUser && (
          <WateenNavigation
            activeTab={activeTab}
            onSelectTab={(tab) => {
              if (tab === 'chats') {
                setActiveConversationId(null);
              }
              setActiveTab(tab);
              soundEffects.playTap();
            }}
            unreadMessagesCount={unreadCount}
            currentUser={currentUser}
          />
        )}

        {/* Dynamic Main View */}
        <main className="flex-1 flex overflow-hidden relative">
          <ErrorBoundary fallbackTitle="تعذّر تحميل هذا القسم">
            {activeTab === 'hub' && currentUser && (
            <WateenHubView
              currentUser={currentUser}
              conversations={deduplicatedConversations}
              onOpenChat={(convId) => {
                setActiveConversationId(convId);
                setActiveTab('chats');
              }}
              onOpenAIModal={() => setShowAIAssistant(true)}
              onOpenStartCall={() => {
                const partner = deduplicatedConversations.find((c) => c.type === 'direct')?.participants?.find((p) => p !== currentUser.username);
                if (partner) {
                  handleStartDirectChat(partner);
                } else {
                  handleStartCall(
                    {
                      username: 'wateen-support',
                      displayName: 'فريق دعم وتين',
                      avatar: 'https://api.dicebear.com/9.x/bottts/svg?seed=WateenSupport',
                    },
                    'voice'
                  );
                }
              }}
              onOpenCreateChat={() => setShowCreateGroup(true)}
              onOpenPremium={() => setShowPremiumModal(true)}
              onNavigateTab={(tab) => setActiveTab(tab)}
            />
          )}

          {activeTab === 'chats' && currentUser && (
            <div className="flex-1 flex overflow-hidden">
              {/* Chats List Column */}
              <div
                className={`w-full md:w-80 lg:w-96 flex flex-col shrink-0 border-l border-slate-800/80 ${
                  activeConversationId ? 'hidden md:flex' : 'flex'
                }`}
              >
                {/* Stories & Moments Tray at top */}
                <StoriesCarousel
                  currentUser={currentUser}
                  stories={stories}
                  onOpenCreateStory={() => setShowCreateStory(true)}
                  onDeleteStory={handleDeleteStory}
                  onEditStory={handleEditStory}
                  onReplyStory={(story, text) => {
                    handleStartDirectChat(story.username);
                    setTimeout(() => {
                      handleSendMessage({
                        text: `رد على قصتك "${story.caption || 'صورة'}": ${text}`,
                      });
                    }, 400);
                  }}
                />

                {/* Conversations List */}
                <div className="flex-1 overflow-hidden">
                  <ConversationsList
                    conversations={deduplicatedConversations}
                    activeConversationId={activeConversationId}
                    onToggleArchive={handleToggleArchive}
                    onDeleteConversation={handleDeleteConversation}
                    onClearConversation={handleClearConversation}
                    onBlockUser={handleBlockUser}
                    onUnblockUser={handleUnblockUser}
                    onSelectConversation={(id) => {
                      setActiveConversationId(id);
                      if (currentUser) {
                        fetch(`/api/conversations/${id}/read`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ username: currentUser.username }),
                        }).catch(() => {});
                      }
                    }}
                    currentUser={currentUser}
                    onlineUsers={onlinePresence}
                    onOpenPremium={() => setShowPremiumModal(true)}
                    onOpenNewChat={() => setShowCreateGroup(true)}
                  />
                </div>
              </div>

              {/* Chat View Column */}
              <div
                className={`flex-1 flex flex-col overflow-hidden ${
                  activeConversationId ? 'flex' : 'hidden md:flex'
                }`}
              >
                {activeConversation ? (
                  <ChatView
                    conversation={activeConversation}
                    messages={activeMessages}
                    currentUser={currentUser}
                    onSendMessage={handleSendMessage}
                    onDeleteMessage={handleDeleteMessage}
                    onReactMessage={handleReactMessage}
                    onPinMessage={handlePinMessage}
                    onUpdateProfile={handleUpdateProfile}
                    onDeleteConversation={handleDeleteConversation}
                    onClearConversation={handleClearConversation}
                    onBlockUser={handleBlockUser}
                    onUnblockUser={handleUnblockUser}
                    onStartCall={(type) => {
                      if (otherUsername) {
                        handleStartCall(
                          {
                            username: otherUsername,
                            displayName: activeConversation.title,
                            avatar: activeConversation.avatar,
                          },
                          type
                        );
                      }
                    }}
                    onOpenProfile={(u) => setSelectedProfileUsername(u)}
                    onSaveToVault={handleSaveToVaultFromChat}
                    onBackMobile={() => setActiveConversationId(null)}
                    isPartnerTyping={isPartnerTyping}
                    partnerOnline={partnerOnline}
                    onTyping={handleTyping}
                  />
                ) : (
                  <div className="flex-1 flex items-center justify-center text-slate-500 text-xs">
                    اختر محادثة لبدء التواصل على وتين
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'stories' && currentUser && (
            <MomentsView
              currentUser={currentUser}
              moments={moments}
              onOpenCreateMoment={() => setShowCreateMoment(true)}
              onLikeMoment={handleLikeMoment}
            />
          )}

          {activeTab === 'discover' && currentUser && (
            <DiscoverView
              currentUser={currentUser}
              channels={deduplicatedConversations.filter((c) => c.type === 'channel')}
              onJoinChannel={(newChan) => {
                setConversations((prev) => [
                  newChan,
                  ...prev.filter((c) => c.id !== newChan.id),
                ]);
                setActiveConversationId(newChan.id);
                setActiveTab('chats');
              }}
              onOpenCreateChannel={() => setShowCreateGroup(true)}
              onSelectConversation={(convId) => {
                setActiveConversationId(convId);
                setActiveTab('chats');
              }}
            />
          )}

          {activeTab === 'vault' && currentUser && (
            <VaultView
              currentUser={currentUser}
              vaultItems={vaultItems}
              onAddVaultItem={handleAddVaultItem}
              onDeleteVaultItem={handleDeleteVaultItem}
            />
          )}

          {activeTab === 'calls' && currentUser && (
            <CallsView
              currentUser={currentUser}
              calls={calls}
              onStartCall={handleStartCall}
            />
          )}

          {activeTab === 'settings' && currentUser && (
            <SettingsView
              currentUser={currentUser}
              appSettings={appSettings}
              onUpdateSettings={handleUpdateSettings}
              onUpdateProfile={handleUpdateProfile}
              onLogout={handleLogout}
              onTerminateSession={handleTerminateSession}
              onLogoutAllDevices={handleLogoutAllDevices}
              onStartDirectChat={handleStartDirectChat}
              onOpenPremium={() => setShowPremiumModal(true)}
            />
          )}

          {activeTab === 'premium' && currentUser && (
            <PremiumView
              currentUser={currentUser}
              appSettings={appSettings}
              onUpdateProfile={handleUpdateProfile}
              onUpdateSettings={handleUpdateSettings}
            />
          )}

          {activeTab === 'admin' && currentUser && (
            <AdminView
              currentUser={currentUser}
              onUpdateProfile={handleUpdateProfile}
            />
          )}
        </ErrorBoundary>
      </main>
      </div>

      {/* 3. MODALS & DIALOGS */}
      {/* Auth / Register / Onboarding Modal */}
      {showAuthModal && (
        <AuthModal
          onLoginSuccess={(user) => {
            setCurrentUser(user);
            setLocalUser(user);
            setShowAuthModal(false);
            soundEffects.playSent();
          }}
        />
      )}

      {/* Active Voice/Video Call Modal */}
      {activeCall && (
        <CallModal callState={activeCall} onEndCall={handleEndCall} />
      )}

      {/* User Profile Modal */}
      {selectedProfileUsername && currentUser && (() => {
        const directConv = conversations.find(
          (c) =>
            c.type === 'direct' &&
            c.participants.includes(currentUser.username) &&
            c.participants.includes(selectedProfileUsername)
        );
        return (
          <UserProfileModal
            username={selectedProfileUsername}
            currentUser={currentUser}
            conversationId={directConv?.id}
            onClose={() => setSelectedProfileUsername(null)}
            onStartChat={handleStartDirectChat}
            onStartCall={handleStartCall}
            onDeleteConversation={handleDeleteConversation}
            onClearConversation={handleClearConversation}
            onBlockUser={handleBlockUser}
            onUnblockUser={handleUnblockUser}
          />
        );
      })()}

      {/* Wateen AI Assistant Dedicated Modal */}
      {showAIAssistant && (
        <AIAssistantModal
          currentUser={currentUser}
          onClose={() => setShowAIAssistant(false)}
          onApplyText={(text) => {
            if (activeConversationId) {
              handleSendMessage({ text });
            }
          }}
          onOpenPremium={() => setShowPremiumModal(true)}
        />
      )}

      {/* Create Story Modal */}
      {showCreateStory && currentUser && (
        <CreateStoryModal
          currentUser={currentUser}
          onClose={() => setShowCreateStory(false)}
          onStoryCreated={handleCreateStory}
        />
      )}

      {/* Create Moment Modal */}
      {showCreateMoment && currentUser && (
        <CreateMomentModal
          currentUser={currentUser}
          onClose={() => setShowCreateMoment(false)}
          onMomentCreated={handleCreateMoment}
        />
      )}

      {/* Create Group / Channel / Direct Chat Modal */}
      {showCreateGroup && currentUser && (
        <CreateGroupModal
          currentUser={currentUser}
          onClose={() => setShowCreateGroup(false)}
          onGroupCreated={handleCreateGroup}
          onStartDirectChat={handleStartDirectChat}
          initialTab="direct"
        />
      )}

      {/* Global Universal Search Modal */}
      {showGlobalSearch && (
        <GlobalSearchModal
          onClose={() => setShowGlobalSearch(false)}
          onSelectUser={(username) => {
            setSelectedProfileUsername(username);
          }}
          onSelectConversation={(convId) => {
            setActiveConversationId(convId);
            setActiveTab('chats');
          }}
          conversations={deduplicatedConversations}
        />
      )}

      {/* Wateen Premium 👑 Modal */}
      {showPremiumModal && currentUser && (
        <PremiumModal
          isOpen={showPremiumModal}
          onClose={() => setShowPremiumModal(false)}
          currentUser={currentUser}
          appSettings={appSettings}
          onUpdateProfile={handleUpdateProfile}
          onUpdateSettings={handleUpdateSettings}
        />
      )}
    </div>
  );
}
 
